import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Spinner } from "bizpandit-ui";
import { ROUTES } from "@/app/constants";
import { useAuthStore } from "@/stores/authStore";
import { pickHomeForRole } from "@/app/router/roleHome";
import { authService } from "@/features/auth/services/auth.service";

/**
 * Public-route guard. Redirects an already-signed-in user to *their own*
 * role-appropriate home so a client who lands on /login doesn't get
 * bounced into the admin shell (that was the old behavior — hard-coded
 * to `/dashboard`).
 */
export function GuestGuard() {
  const { user } = useAuthStore();
  const location = useLocation();

  if (user) {
    if (user.status === "Pending Approval") {
      return <Navigate to={ROUTES.pendingApproval} replace />;
    }
    /**
     * The MCP OAuth handoff, for a user who is ALREADY signed in.
     *
     * The authorize page 302s here when the bp_access_token cookie has
     * expired (the SPA silently refreshes it; a backend page cannot), and
     * this guard used to bounce straight to the role home, dropping the
     * ?redirect= - which is how clicking Connect in Claude landed the owner
     * on the Catalogue with no consent page. By the time this code runs the
     * app's own /auth/me call has refreshed the cookie, so following the
     * redirect hands authorize a fresh one.
     *
     * Same STRICT prefix as useLogin: exactly one path family may leave the
     * SPA, so this can never become an open redirect. window.location, not
     * Navigate - /api/* is not an SPA route.
     */
    const redirectParam = new URLSearchParams(location.search).get("redirect");
    if (redirectParam?.startsWith("/api/mcp/oauth/authorize")) {
      return <McpAuthorizeBridge redirect={redirectParam} />;
    }
    return <Navigate to={pickHomeForRole(user.role)} replace />;
  }

  return <Outlet />;
}

/**
 * The bounce back to the authorize page, made loop-proof.
 *
 * authStore is PERSISTED, so  exists the instant the page loads -
 * before anything has refreshed the expired cookie that sent us here. Leaving
 * immediately would hand authorize the same stale cookie and ping-pong
 * forever. One live  first: the interceptor refreshes the cookie
 * on its 401, success means authorize will accept us, and failure means the
 * session is truly dead - clear it and show the login form, which carries the
 * redirect on through useLogin.
 */
function McpAuthorizeBridge({ redirect }: { redirect: string }) {
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    let cancelled = false;
    authService
      .getMe()
      .then(() => {
        if (!cancelled) window.location.assign(redirect);
      })
      .catch(() => {
        if (!cancelled) logout();
      });
    return () => {
      cancelled = true;
    };
  }, [redirect, logout]);

  return (
    <div className="min-h-dvh flex items-center justify-center">
      <Spinner />
    </div>
  );
}
